import { type Editor } from '@tiptap/react';
import clsx from 'clsx';
import { BlockSelector } from './block-selector';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline';

export function FormattingToolbar({
  editor,
  className,
}: {
  editor: Editor | null | undefined;
  className?: string;
}) {
  // Render toolbar even when editor is null so tests can assert presence
  // of buttons; buttons should be inert/disabled in that case.

  const formatButtons = [
    {
      icon: BoldIcon,
      label: 'Bold',
      isActive: editor?.isActive('bold') ?? false,
      onClick: () => editor?.chain().focus().toggleBold().run(),
      shortcut: 'Cmd+B',
    },
    {
      icon: ItalicIcon,
      label: 'Italic',
      isActive: editor?.isActive('italic') ?? false,
      onClick: () => editor?.chain().focus().toggleItalic().run(),
      shortcut: 'Cmd+I',
    },
    {
      icon: UnderlineIcon,
      label: 'Underline',
      isActive: editor?.isActive('underline') ?? false,
      onClick: () => editor?.chain().focus().toggleUnderline().run(),
      shortcut: 'Cmd+U',
    },
  ];

  const listButtons = [
    {
      label: 'Ordered List',
      onClick: () => editor?.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Bullet List',
      onClick: () => editor?.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Decrease Indent',
      onClick: () =>
        editor?.can().chain().focus().liftListItem('listItem').run(),
    },
    {
      label: 'Increase Indent',
      onClick: () =>
        editor?.can().chain().focus().sinkListItem('listItem').run(),
    },
  ];

  return (
    <div
      className={clsx(
        'flex items-center gap-3 p-2 border-b border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-gray-800/50',
        className,
      )}
    >
      {/* Block Selector */}
      <BlockSelector editor={editor} />

      {/* Divider */}
      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />

      {/* Format Buttons */}
      <div className="flex items-center gap-1">
        {formatButtons.map((button) => (
          <button
            key={button.label}
            type="button"
            onClick={button.onClick}
            title={`${button.label} (${button.shortcut})`}
            aria-label={button.label}
            disabled={!editor}
            className={clsx(
              'inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-150',
              'hover:bg-gray-200 dark:hover:bg-gray-700',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
              button.isActive
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400',
            )}
          >
            <button.icon className="w-4 h-4" />
          </button>
        ))}
        {/* List Buttons */}
        {listButtons.map((b) => (
          <button
            key={b.label}
            type="button"
            onClick={b.onClick}
            title={b.label}
            aria-label={b.label}
            disabled={!editor}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-150 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
          >
            <span className="sr-only">{b.label}</span>
            <svg className="w-4 h-4" aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
